/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, VbzDivider } from '@smiles/voebiz-ui-kit';
import { isMobile } from 'react-device-detect';
import { GetCorporateClub, PlanListType } from '../../services/types/type';
import { IValues } from '../../components/TableBeneficiaries/TableSearch/types/type';
import { IMember, IPlanList } from '../../types';
import { AddIcon } from '../../components/Icons/AddIcon';
import { EditIcon } from '../../components/Icons/EditIcon';
import ModalDefault, { IModal } from '../../components/Modal/ModalDefault';
import ModalAddingBeneficiaries from '../../components/Modal/ModalAddingBeneficiaries';
import ModalRemovePlan from '../../components/Modal/ModalRemovePlan';
import ProfileDesktop from '../../components/Profile/ProfileDesktop';
import ProfileMobile from '../../components/Profile/ProfileMobile';
import { removeOneBeneficiariePlans, updateBeneficiary, getBeneficiariesList } from '../../services/service';
import { capitalize } from '../../utils/capitalize';
import { parseJwt } from '../../utils/decodeJWT';
import { setTimerWithSavedTime } from '../../utils/setTimerWithSavedTime';
import {
  checkIfIsValidResponse,
  checkIfNeedConfirmUpdate,
  defaultModalPropsDefaults,
  openModalError,
  renderPlans,
} from './helper';
import { useSelectedPlans } from '../../contexts/SelectedPlans';

interface PlanList {
  id: string;
  desc: string;
  active: string;
  miles: string;
}

const getToken = parseJwt(`${localStorage.getItem('session-token')}`);

const memberNumVoeBiz = getToken?.sub.split('|')[2];

const CorpClubProfile = () => {
  const { t } = useTranslation('corpClubProfile');
  const [openModalBeneficiaries, setOpenModalBeneficiaries] = useState(false);
  const [buttonEdit, setButtonEdit] = useState(false);
  const [openRemovePlanModal, setOpenRemovePlanModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [editionBlocked, setEditionBlocked] = useState(false);
  const [member, setMember] = useState<IMember>({
    memberNumVoeBiz,
    cpf: '',
    name: '',
    created: '',
    birthdate: '',
    email: '',
    status: '',
    planList: [],
    currentPlan: {
      id: '',
      desc: '',
      miles: '',
    },
  });
  const [defaultModalProps, setDefaultModalProps] = useState<IModal>(defaultModalPropsDefaults);
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isContractedPlanRemoved, setIsContractedPlanRemoved] = useState(false);
  const [isAddBenefitProfile, setIsAddBenefitProfile] = useState(false);

  const { selectedPlans, setSelectedPlans, clearPlans } = useSelectedPlans();

  useEffect(() => {
    (async () => {
      if (member.cpf.length === 0) {
        const responseMember = await getBeneficiariesList({ search: searchParams.get('cpf') || '' });
        if (responseMember?.corporateClubList?.length > 0) {
          const memberData = responseMember.corporateClubList[0];
          setMember(memberData);
        }
      }
    })();
  }, [searchParams]);

  const toggleModalBeneficiaries = () => {
    setOpenModalBeneficiaries(false);
    setButtonEdit(false);
  };

  const onToggleRemovePlanModal = useCallback((isOpen: boolean) => {
    setOpenRemovePlanModal(isOpen);
  }, []);

  const removeOneBeneficiariePlansHandler = async () => {
    // serviço de excluir benefícios

    const response = await removeOneBeneficiariePlans({ cpf: member.cpf, memberNumVoeBiz });

    if (checkIfIsValidResponse(response)) {
      const responseMember = await getBeneficiariesList({ search: member.cpf });

      if (responseMember?.corporateClubList?.length > 0) {
        setMember(responseMember.corporateClubList[0] as IMember);
      }
    } else {
      const secondClick = async () => {
        setDefaultModalProps((prevState) => ({ ...prevState, isOpen: false }));
        await removeOneBeneficiariePlansHandler();
      };

      openModalError({ setDefaultModalProps, component: 'defaultModalRemoveError', secondClick });
    }
  };

  const updateChargeList = async ({ values }: IValues) => {
    setIsLoading(true);

    const miles = values.map((mile: string) => ({
      miles: mile,
    }));

    setSelectedPlans(miles);
  };

  const addBeneficiaryHandler = async () => {
    setIsLoading(true);

    const payload: GetCorporateClub = {
      cpf: member.cpf,
      memberNumVoeBiz,
      planList: [...selectedPlans] as PlanListType[],
    };

    const response = await updateBeneficiary(payload);

    toggleModalBeneficiaries();
    setShowConfirmUpdate(false);

    if (checkIfIsValidResponse(response)) {
      setDefaultModalProps(() => ({
        component: showConfirmUpdate ? 'anotherModalSuccess' : 'defaultModalSuccess',
        imageClass: 'vbz-icon-corporates-club-beach-chair',
        isOpen: true,
        onClick: () => setDefaultModalProps((prevState) => ({ ...prevState, isOpen: false })),
        secondClick: () => setDefaultModalProps((prevState) => ({ ...prevState, isOpen: false })),
      }));

      const responseMember = await getBeneficiariesList({ search: member.cpf });

      if (responseMember?.corporateClubList?.length > 0) {
        setMember(responseMember.corporateClubList[0] as IMember);
      }
    } else {
      const secondClick = () => setDefaultModalProps((prevState) => ({ ...prevState, isOpen: false }));
      openModalError({ setDefaultModalProps, component: 'defaultModalError', secondClick });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (selectedPlans.length > 0) {
      const needConfirm = checkIfNeedConfirmUpdate({ member, selectedPlans });
      if (needConfirm) {
        setIsLoading(false);
        toggleModalBeneficiaries();
        setShowConfirmUpdate(true);
      } else {
        addBeneficiaryHandler();
      }
    }
  }, [selectedPlans]);

  const toggleConfirmModal = (open: boolean) => {
    if (open) clearPlans();
    setShowConfirmUpdate(open);
  };

  const openAddModal = () => {
    clearPlans();
    setOpenModalBeneficiaries(true);
    setIsAddBenefitProfile(true);
  };

  useEffect(() => {
    const localStorageBlockerTimer = localStorage.getItem('block-timer');
    if (localStorageBlockerTimer) {
      setEditionBlocked(true);
      setTimerWithSavedTime(Number(localStorageBlockerTimer), setEditionBlocked);
    }
  }, []);

  return (
    <div className="corp-club-profile-container" data-testid="corp-club-profile-test">
      <section className="header-profile">
        <h3 className="header-profile-title">{t('title')}</h3>
        <p className="header-profile-subtitle">{t('subtitle')}</p>
      </section>
      <section className="mb-5 body-profile">
        <div className="body-profile-data">
          <div className="body-profile-data-name">
            <h4>{capitalize(member.name)}</h4>
          </div>
          <VbzDivider />
          {!isMobile ? (
            <ProfileDesktop
              created={member.created}
              birthdate={member.birthdate}
              email={member.email}
              cpf={member.cpf}
            />
          ) : (
            <ProfileMobile
              created={member.created}
              birthdate={member.birthdate}
              email={member.email}
              cpf={member.cpf}
            />
          )}
        </div>
        <div className="body-profile-plans">
          <div className="mb-4 body-profile-plans-available">
            <span>{t('available_plans_title')}</span>
            <div className="d-flex flex-wrap">
              {member.planList.length > 0 ? (
                member.planList
                  .sort((a: PlanList, b: PlanList) => {
                    if (+a.miles < +b.miles) {
                      return -1;
                    }
                    if (+a.miles > +b.miles) {
                      return 1;
                    }

                    return 0;
                  })
                  .map((plan: IPlanList, index) => {
                    const totalPlans = member.planList.length;
                    const content = new Intl.NumberFormat('pt-BR').format(Number(plan.miles));
                    return (
                      <span className="mr-1" key={plan.id}>
                        {renderPlans(index, totalPlans, content)}
                      </span>
                    );
                  })
              ) : (
                <p>Nenhum</p>
              )}
            </div>
          </div>
          <div className="body-profile-plans-contracted">
            <span>{t('contracted_plan_title')}</span>
            <p>
              {member?.currentPlan?.miles?.length > 0
                ? `Plano ${new Intl.NumberFormat().format(Number(member.currentPlan.miles))}`
                : 'Nenhum'}
            </p>
          </div>
          <div
            className={
              editionBlocked ? 'edition-blocked-banner-wrapper' : 'hide-edition-blocker edition-blocked-banner-wrapper'
            }
          >
            <div className="edition-blocked-banner">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M14 5H10V6.33333H14V5ZM11.3333 13.6667H12.6667V9.66667H11.3333V13.6667ZM16.6867 9.26L17.6333 8.31333C17.3467 7.97333 17.0333 7.65333 16.6933 7.37333L15.7467 8.32C14.7133 7.49333 13.4133 7 12 7C8.68667 7 6 9.68667 6 13C6 16.3133 8.68 19 12 19C15.32 19 18 16.3133 18 13C18 11.5867 17.5067 10.2867 16.6867 9.26ZM12 17.6667C9.42 17.6667 7.33333 15.58 7.33333 13C7.33333 10.42 9.42 8.33333 12 8.33333C14.58 8.33333 16.6667 10.42 16.6667 13C16.6667 15.58 14.58 17.6667 12 17.6667Z"
                  fill="#FC2C5E"
                />
              </svg>
            </div>
            <span className="edition-blocked-banner-text">Aguarde 30 min para fazer uma nova edição</span>
          </div>
          <div className="body-profile-plans-button-container">
            {member.planList.length > 0 ? (
              <>
                <Button
                  className="body-profile-plans-button-container-btn"
                  icon={<EditIcon />}
                  onClick={() => setButtonEdit(true)}
                  disabled={editionBlocked}
                >
                  <span>{t('button_edit')}</span>
                </Button>
                <button className="exclude-button" onClick={() => setOpenRemovePlanModal(true)} type="button">
                  <div className="vbz-icon-corporates-club-trash" />
                  <span className="ml-3">{t('button_delete')}</span>
                </button>
              </>
            ) : (
              <Button className="body-profile-plans-button-container-btn" icon={<AddIcon />} onClick={openAddModal}>
                <span>{t('button_text')}</span>
              </Button>
            )}
          </div>
        </div>
      </section>
      <div className="profile-return-btn-wrapper">
        <Button
          onClick={() => {
            navigate(`/clube-smiles`);
          }}
          className="btn-return-profile"
          data-testid="profile-return-btn"
        >
          <span>Voltar</span>
        </Button>
      </div>
      <ModalDefault {...defaultModalProps} />
      <ModalAddingBeneficiaries
        isAddBenefitButton={openModalBeneficiaries}
        isBeneficiariesButton={false}
        closeModalBeneficiaries={toggleModalBeneficiaries}
        isEditButtonProfile={buttonEdit}
        profile={member as any}
        addBeneficiaryHandler={updateChargeList}
        isLoading={isLoading}
        setIsContractedPlanRemoved={setIsContractedPlanRemoved}
        isAddBenefitProfile={isAddBenefitProfile}
      />
      <ModalRemovePlan
        onClick={removeOneBeneficiariePlansHandler}
        isOpen={openRemovePlanModal}
        toggleModal={onToggleRemovePlanModal}
        component="modalRemoveBenefit"
        buttonText="btn_delete"
      />
      <ModalRemovePlan
        onClick={addBeneficiaryHandler}
        isOpen={showConfirmUpdate}
        toggleModal={toggleConfirmModal}
        component="modalRemovePlan"
        isLoading={isLoading}
      />
    </div>
  );
};

export default CorpClubProfile;
